import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import ControlledDateField from "../../components/controlledComponents/ControlledDateField";
import {
  TCompliance,
  TComplianceCategory,
  TComplianceFrequency,
  COMPLIANCE_STATUSES,
  COMPLIANCE_STATUS_LABELS,
  TComplianceStatus,
} from "../../../apis/compliance/compliance.interfaces";
import {
  useCreateComplianceHook,
  useUpdateComplianceHook,
} from "../../../hooks/useComplianceHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";

const complianceSchema = z.object({
  name: z
    .string()
    .min(1, "Compliance name is required")
    .min(2, "Compliance name must be at least 2 characters")
    .max(200, "Compliance name must not exceed 200 characters"),
  category: z.enum(["regulatory", "institutional", "accreditation", "other"], {
    errorMap: () => ({ message: "Please select a valid category" }),
  }),
  description: z
    .string()
    .max(1000, "Description must not exceed 1000 characters")
    .optional()
    .or(z.literal("")),
  frequency: z
    .enum(["annual", "biannual", "quarterly", "monthly", "weekly", "custom"], {
      errorMap: () => ({ message: "Please select a valid frequency" }),
    })
    .optional()
    .nullable(),
  dueDate: z.custom<Dayjs | null>().optional().nullable(),
  status: z.enum(COMPLIANCE_STATUSES, {
    errorMap: () => ({ message: "Please select a valid status" }),
  }),
});

type ComplianceFormData = z.infer<typeof complianceSchema>;

interface ManageComplianceDialogProps {
  open: boolean;
  onClose: () => void;
  compliance?: TCompliance | null;
}

const ManageComplianceDialog: FC<ManageComplianceDialogProps> = ({
  open,
  onClose,
  compliance,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const createComplianceMutation = useCreateComplianceHook();
  const updateComplianceMutation = useUpdateComplianceHook();
  const isEditMode = !!compliance;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ComplianceFormData>({
    resolver: zodResolver(complianceSchema),
    defaultValues: {
      name: "",
      category: "regulatory",
      description: "",
      frequency: undefined,
      dueDate: null,
      status: "PENDING",
    },
  });

  useEffect(() => {
    if (compliance) {
      reset({
        name: compliance.name,
        category: compliance.category,
        description: compliance.description || "",
        frequency: compliance.frequency || undefined,
        dueDate: compliance.dueDate ? dayjs(compliance.dueDate) : null,
        status: compliance.status,
      });
    } else {
      reset({
        name: "",
        category: "regulatory",
        description: "",
        frequency: undefined,
        dueDate: null,
        status: "PENDING",
      });
    }
  }, [compliance, reset, open]);

  const onSubmit = async (data: ComplianceFormData) => {
    if (!selectedInstitute) {
      logger.show_error("Please select an institute first");
      return;
    }

    try {
      if (isEditMode && compliance) {
        await updateComplianceMutation.mutateAsync({
          id: compliance.id,
          request: {
            name: data.name,
            category: data.category,
            description: data.description || undefined,
            frequency: data.frequency || undefined,
            dueDate: data.dueDate ? data.dueDate.toISOString() : undefined,
            status: data.status,
          },
        });
        logger.show_success("Compliance updated successfully!");
      } else {
        await createComplianceMutation.mutateAsync({
          instituteId: selectedInstitute.id,
          name: data.name,
          category: data.category,
          description: data.description || undefined,
          frequency: data.frequency || undefined,
          dueDate: data.dueDate ? data.dueDate.toISOString() : undefined,
          status: data.status,
        });
        logger.show_success("Compliance created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} compliance. Please try again.`,
      );
    }
  };

  const categoryOptions: { key: TComplianceCategory; value: string }[] = [
    { key: "regulatory", value: "Regulatory" },
    { key: "institutional", value: "Institutional" },
    { key: "accreditation", value: "Accreditation" },
    { key: "other", value: "Other" },
  ];

  const frequencyOptions: { key: TComplianceFrequency; value: string }[] = [
    { key: "annual", value: "Annual" },
    { key: "biannual", value: "Biannual" },
    { key: "quarterly", value: "Quarterly" },
    { key: "monthly", value: "Monthly" },
    { key: "weekly", value: "Weekly" },
    { key: "custom", value: "Custom" },
  ];

  const statusOptions: { key: TComplianceStatus; value: string }[] =
    COMPLIANCE_STATUSES.map((key) => ({
      key,
      value: COMPLIANCE_STATUS_LABELS[key],
    }));

  const isLoading =
    isSubmitting ||
    createComplianceMutation.isPending ||
    updateComplianceMutation.isPending;
  const isError =
    createComplianceMutation.isError || updateComplianceMutation.isError;
  const errorMessage =
    createComplianceMutation.error?.message ||
    updateComplianceMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-compliance-dialog"
    >
      <DialogTitle data-testid="manage-compliance-dialog-title">
        {isEditMode ? "Edit Compliance" : "Add New Compliance"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="compliance-form">
        <DialogContent data-testid="compliance-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="compliance-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} compliance. Please try again.`}
            </Alert>
          )}

          <Grid
            container
            spacing={3}
            sx={{ mt: 1 }}
            data-testid="compliance-form-container"
          >
            <ControlledTextField
              control={control as any}
              name="name"
              id="compliance-name"
              label="Compliance Name"
              required
              errorMessage={errors.name?.message}
            />

            <ControlledSelectField
              control={control as any}
              name="category"
              id="compliance-category"
              label="Category"
              itemList={categoryOptions}
              isRequired
              errorMessage={errors.category?.message}
            />

            <ControlledSelectField
              control={control as any}
              name="frequency"
              id="compliance-frequency"
              label="Frequency"
              itemList={frequencyOptions}
              errorMessage={errors.frequency?.message}
            />

            <ControlledDateField
              control={control as any}
              name="dueDate"
              id="compliance-due-date"
              label="Due Date"
              errorMessage={errors.dueDate?.message as string}
            />

            <ControlledSelectField
              control={control as any}
              name="status"
              id="compliance-status"
              label="Status"
              itemList={statusOptions}
              isRequired
              errorMessage={errors.status?.message}
            />

            <Grid item xs={9}>
              <ControlledTextField
                control={control as any}
                name="description"
                id="compliance-description"
                label="Description"
                multiline
                rows={3}
                errorMessage={errors.description?.message}
                isGridItem={false}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions data-testid="compliance-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-compliance-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-compliance-button"
          >
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Compliance"
                : "Create Compliance"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageComplianceDialog;
